import React, { useState, useEffect, useRef } from 'react';
import { Card, CardBody, CardHeader, Col, Row, Table, Badge, Button, Input, Label, Spinner, Container } from 'reactstrap';
import { H5, H6 } from '../../../AbstractElements';
import { ArrowLeft, Printer, Search, Truck, User, FileText, Tool, Calendar, DollarSign, Shield, Phone, Mail, Grid, TrendingDown, MapPin, CheckCircle, AlertTriangle, Download } from 'react-feather';
import { useReactToPrint } from 'react-to-print';

import { apiPost } from '../../../Utils';
import Breadcrumbs from '../../../CommonElements/Breadcrumbs';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useAuth } from '../../../Context/AuthContext';
const VehicleWiseReport = () => {
    const [selectedVehicle, setSelectedVehicle] = useState('');
    const [vehicleList, setVehicleList] = useState<any[]>([]);
    const [reportData, setReportData] = useState<any>(null);
    const [branches, setBranches] = useState<any[]>([]);
    const [allVehiclesForDropdown, setAllVehiclesForDropdown] = useState<any[]>([]);

    // Filters
    const [searchTerm, setSearchTerm] = useState('');
    const [branchId, setBranchId] = useState('');
    const [filterVehicleId, setFilterVehicleId] = useState('');
    const { hasPermission } = useAuth();
    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [lastPage, setLastPage] = useState(1);
    const [totalVehicles, setTotalVehicles] = useState(0);

    const [loading, setLoading] = useState(true);
    const [reportLoading, setReportLoading] = useState(false);

    const componentRef = useRef(null);

    const handleDownload = () => {
        const doc = new jsPDF() as any;

        // --- Header ---
        doc.setFontSize(22);
        doc.setTextColor(40, 40, 40);
        doc.text("Vehicle Report", 14, 20);

        doc.setFontSize(10);
        doc.setTextColor(100);
        doc.text(`Reg No: ${reportData.vehicleInfo.reg_expiry}`, 14, 26);
        doc.text(`Generated on: ${new Date().toLocaleDateString('en-GB')}`, 14, 31);

        doc.setFontSize(10);
        doc.setTextColor(0);
        doc.text("Company Name", 196, 20, { align: "right" });
        doc.setTextColor(100);
        doc.text("Fleet Pro Dept.", 196, 25, { align: "right" });
        doc.text("Dubai, UAE", 196, 30, { align: "right" });

        let currentY = 45;

        // --- Summary Overview (Moved to Top) ---
        doc.setFontSize(12);
        doc.setTextColor(0);
        doc.text("Summary Overview", 14, currentY);
        currentY += 8;

        const stats = [
            ["Total Expenses", reportData.totalExpenses],
            ["Vehicle Year", reportData.vehicleInfo.year],
            ["Branch", reportData.vehicleInfo.branch],
            ["Status", reportData.vehicleInfo.status]
        ];

        autoTable(doc, {
            startY: currentY,
            head: [['Metric', 'Value']],
            body: stats,
            theme: 'striped',
            headStyles: { fillColor: [66, 139, 202] },
            columnStyles: { 0: { fontStyle: 'bold', cellWidth: 50 } }
        });

        currentY = (doc as any).lastAutoTable.finalY + 15;

        // --- Vehicle Details ---
        doc.setFontSize(14);
        doc.setTextColor(0);
        doc.text("Vehicle Details", 14, currentY);
        currentY += 8;

        doc.setFontSize(10);
        doc.setTextColor(100);
        const details = [
            `Brand & Model: ${reportData.vehicleInfo.make} ${reportData.vehicleInfo.model}`,
            `VIN: ${reportData.vehicleInfo.vin || 'N/A'}`,
            `Fuel & Color: ${reportData.vehicleInfo.fuel_type} • ${reportData.vehicleInfo.color}`,
            `Category: ${reportData.vehicleInfo.category}`,
            `Reg Expiry: ${reportData.vehicleInfo.reg_expiry ? new Date(reportData.vehicleInfo.reg_expiry).toLocaleDateString('en-GB') : 'N/A'}`,
            `Purchase Date: ${reportData.vehicleInfo.purchase_date ? new Date(reportData.vehicleInfo.purchase_date).toLocaleDateString('en-GB') : 'N/A'}`,
            `Ins Provider: ${reportData.vehicleInfo.ins_provider}`,
            `Ins Policy: ${reportData.vehicleInfo.ins_policy || 'N/A'}`,
            `Owner: ${reportData.vehicleInfo.owner_name}`,
            `Owner Contact: ${reportData.vehicleInfo.owner_mobile}`,
            `Owner Email: ${reportData.vehicleInfo.owner_email}`
        ];

        let col = 0;
        details.forEach((detail, index) => {
            if (index % 2 === 0 && index !== 0) {
                currentY += 6;
                col = 0;
            }
            const x = col === 0 ? 14 : 105;
            doc.text(detail, x, currentY);
            col = 1;
        });
        currentY += 15;

        // --- Expense History ---
        doc.setFontSize(12); // Reset Font Size for Section Headers
        doc.setTextColor(0);
        doc.text("Expense History", 14, currentY);
        currentY += 6;
        const expenseRows = reportData.expenses?.map((e: any) => [e.date ? new Date(e.date).toLocaleDateString('en-GB') : '-', e.reg_no, e.type, e.amount]) || [];
        autoTable(doc, {
            startY: currentY,
            head: [['Date', 'Vehicle No', 'Type', 'Amount']],
            body: expenseRows.length ? expenseRows : [['No Expenses Found', '', '', '']],
            theme: 'grid',
            headStyles: { fillColor: [40, 167, 69] }
        });
        currentY = (doc as any).lastAutoTable.finalY + 15;

        // --- Upcoming Renewals ---
        doc.text("Upcoming Renewals & Services", 14, currentY);
        currentY += 6;
        const upcomingRows = reportData.upcoming?.map((u: any) => [u.reg_no, u.date ? new Date(u.date).toLocaleDateString('en-GB') : '-', u.type]) || [];
        autoTable(doc, {
            startY: currentY,
            head: [['Vehicle No', 'Next Date', 'Type']],
            body: upcomingRows.length ? upcomingRows : [['No Upcoming Events', '', '']],
            theme: 'grid',
            headStyles: { fillColor: [255, 193, 7] }
        });
        currentY = (doc as any).lastAutoTable.finalY + 15;

        // --- Driver Usage History ---
        doc.text("Driver Usage History", 14, currentY);
        currentY += 6;
        const driverRows = reportData.driverHistory.map((d: any) => [d.driver, `${d.from} to ${d.to}`, d.start_odometer, d.end_odometer || '-', d.duration]);
        autoTable(doc, {
            startY: currentY,
            head: [['Driver', 'Range', 'Start Odometer', 'End Odometer', 'Duration']],
            body: driverRows.length ? driverRows : [['No History Found', '', '', '', '']],
            theme: 'grid',
            headStyles: { fillColor: [100, 100, 100] }
        });
        currentY = (doc as any).lastAutoTable.finalY + 15;

        // --- Accident History ---
        doc.text("Accident History", 14, currentY);
        currentY += 6;
        const accidentRows = reportData.accidents.map((a: any) => [a.date ? new Date(a.date).toLocaleDateString('en-GB') : '-', a.description, a.cost]);
        autoTable(doc, {
            startY: currentY,
            head: [['Date', 'Description', 'Cost']],
            body: accidentRows.length ? accidentRows : [['No Accidents Found', '', '']],
            theme: 'grid',
            headStyles: { fillColor: [220, 53, 69] }
        });
        currentY = (doc as any).lastAutoTable.finalY + 15;

        doc.save(`VehicleReport-${reportData.vehicleInfo.reg_expiry}.pdf`);
    };

    const handlePrint = useReactToPrint({
        content: () => componentRef.current,
    });

    // Fetch Branches on Load
    useEffect(() => {
        apiPost("branches/list").then((res: any) => {
            setBranches(Array.isArray(res) ? res : []);
        }).catch(err => console.error(err));
    }, []);

    // Fetch Vehicles for Dropdown
    useEffect(() => {
        const payload: any = {};
        if (branchId) payload.branch_id = branchId;
        apiPost("vehicles/list", payload).then((res: any) => {
            setAllVehiclesForDropdown(Array.isArray(res) ? res : []);
        }).catch(() => setAllVehiclesForDropdown([]));
    }, [branchId]);


    // Fetch Vehicles List (Paginated)
    const fetchVehicles = () => {
        setLoading(true);
        const payload: any = {
            page: currentPage,
            per_page: 15,
            search: searchTerm
        };
        if (branchId) payload.branch_id = branchId;
        // If specific vehicle is selected in filter, we can filter by it or just show it
        if (filterVehicleId) payload.search = allVehiclesForDropdown.find(v => v.id === parseInt(filterVehicleId))?.registration_number || '';

        apiPost("vehicles/report_list", payload).then((res: any) => {
            if (res && res.data) {
                setVehicleList(res.data);
                setCurrentPage(res.current_page);
                setLastPage(res.last_page);
                setTotalVehicles(res.total);
            }
            setLoading(false);
        }).catch(() => setLoading(false));
    };

    useEffect(() => {
        fetchVehicles();
    }, [currentPage, branchId, filterVehicleId, searchTerm]);

    // Fetch Report Data when Vehicle Selected
    useEffect(() => {
        if (selectedVehicle) {
            setReportLoading(true);
            setReportData(null);
            const payload: any = { id: selectedVehicle };

            apiPost("reports/vehicle-overview", payload).then((res: any) => {
                if (res) setReportData(res);
                setReportLoading(false);
            }).catch(() => setReportLoading(false));
        }
    }, [selectedVehicle]);

    const handlePageChange = (newPage: number) => {
        if (newPage >= 1 && newPage <= lastPage) {
            setCurrentPage(newPage);
        }
    };



    return (
        <div className="page-body">
            <Breadcrumbs pageTitle={selectedVehicle ? "Vehicle Report" : "Vehicle Selection"} parent="Reports" title={selectedVehicle ? "Report Details" : "Vehicle Wise"} />
            <div className="container-fluid">
                {!selectedVehicle ? (
                    <>
                        {/* Filters Section */}
                        <Card className="mb-4 border-0 shadow-none" style={{ background: 'rgba(255, 255, 255, 0.5)', backdropFilter: 'blur(10px)', borderRadius: '15px' }}>
                            <CardBody>
                                <Row className="g-3">
                                    <Col md={4}>
                                        <Label className="small text-secondary fw-semibold mb-1 d-flex align-items-center gap-1">
                                            <MapPin size={14} /> Select Branch
                                        </Label>
                                        <Input
                                            type="select"
                                            value={branchId}
                                            onChange={(e) => setBranchId(e.target.value)}
                                            bsSize="sm"
                                            className="form-control-light border-0 bg-white shadow-sm"
                                            style={{ borderRadius: '8px', padding: '8px 12px' }}
                                        >
                                            <option value="">All Branches</option>
                                            {branches.map((b: any) => (
                                                <option key={b.id} value={b.id}>{b.branch_name}</option>
                                            ))}
                                        </Input>
                                    </Col>
                                    <Col md={4}>
                                        <Label className="small text-secondary fw-semibold mb-1 d-flex align-items-center gap-1">
                                            <Truck size={14} /> Filter Vehicle
                                        </Label>
                                        <Input
                                            type="select"
                                            value={filterVehicleId}
                                            onChange={(e) => setFilterVehicleId(e.target.value)}
                                            bsSize="sm"
                                            className="form-control-light border-0 bg-white shadow-sm"
                                            style={{ borderRadius: '8px', padding: '8px 12px' }}
                                        >
                                            <option value="">All Vehicles</option>
                                            {allVehiclesForDropdown.map((v: any) => (
                                                <option key={v.id} value={v.id}>{v.registration_number} - {v.model}</option>
                                            ))}
                                        </Input>
                                    </Col>
                                    <Col md={4}>
                                        <Label className="small text-secondary fw-semibold mb-1 d-flex align-items-center gap-1">
                                            <Search size={14} /> Quick Search
                                        </Label>
                                        <Input
                                            type="text"
                                            placeholder="Search..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            bsSize="sm"
                                            className="form-control-light border-0 bg-white shadow-sm"
                                            style={{ borderRadius: '8px', padding: '8px 12px' }}
                                        />
                                    </Col>
                                </Row>
                            </CardBody>
                        </Card>

                        {/* List Section */}
                        {loading ? (
                            <div className="d-flex justify-content-center align-items-center w-100" style={{ minHeight: '50vh' }}>
                                <Spinner color="primary" />
                            </div>
                        ) : (
                            <>
                                {vehicleList.length > 0 ? (
                                    <Row className="row-cols-1 row-cols-sm-2 row-cols-md-3 row-cols-lg-4 row-cols-xl-5 g-3">
                                        {vehicleList.map((v) => (
                                            <Col key={v.id}>
                                                <Card
                                                    className="h-100 border-0 shadow-sm vehicle-card"
                                                    style={{
                                                        cursor: 'pointer',
                                                        transition: 'all 0.25s cubic-bezier(0.02, 0.01, 0.47, 1)',
                                                        borderRadius: '8px',
                                                        overflow: 'hidden',
                                                        borderLeft: '4px solid ' + (v.status === 'Active' ? '#7366ff' : '#f73164'),
                                                        background: 'linear-gradient(135deg, #ffffff 0%, #f1f5fa 100%)'
                                                    }}
                                                    onClick={() => setSelectedVehicle(v.id)}
                                                    onMouseEnter={(e) => {
                                                        e.currentTarget.style.transform = 'translateY(-3px)';
                                                        e.currentTarget.style.boxShadow = '0 10px 20px rgba(0,0,0,0.05)';
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        e.currentTarget.style.transform = 'translateY(0)';
                                                        e.currentTarget.style.boxShadow = '';
                                                    }}
                                                >
                                                    <CardBody className="p-3">
                                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                                            <span className="text-uppercase fw-bold text-muted" style={{ fontSize: '0.7rem', letterSpacing: '1px' }}>
                                                                {v.brand} {v.model}
                                                            </span>
                                                            {v.status === 'Active' && <span className="badge bg-light-primary text-primary" style={{ fontSize: '0.65rem' }}>ACTIVE</span>}
                                                            {v.status === 'Inactive' && <span className="badge bg-light-secondary text-secondary" style={{ fontSize: '0.65rem' }}>INACTIVE</span>}
                                                            {v.status === 'Maintenance' && <span className="badge bg-light-warning text-warning" style={{ fontSize: '0.65rem' }}>MAINTENANCE</span>}
                                                            {v.status === 'Sold' && <span className="badge bg-light-danger text-danger" style={{ fontSize: '0.65rem' }}>SOLD</span>}
                                                            {v.status === 'Destroyed' && <span className="badge bg-light-dark text-white" style={{ fontSize: '0.65rem' }}>DESTROYED</span>}

                                                        </div>
                                                        <div className="d-flex align-items-center justify-content-between">
                                                            <div className="bg-light rounded px-2 py-1 font-monospace fw-bold text-dark border-start border-3" style={{ fontSize: '1rem', borderColor: '#2c323f' }}>
                                                                {v.registration_number}
                                                            </div>
                                                            <div className={`rounded-circle p-2 ${v.status === 'Active' ? 'text-primary' : 'text-warning'}`} style={{ backgroundColor: 'rgba(0,0,0,0.02)' }}>
                                                                <Truck size={18} />
                                                            </div>
                                                        </div>
                                                    </CardBody>
                                                </Card>
                                            </Col>
                                        ))}
                                    </Row>
                                ) : (
                                    <div className="d-flex flex-column justify-content-center align-items-center w-100" style={{ minHeight: '50vh' }}>
                                        <div className="text-center text-muted opacity-50">
                                            <Search size={48} className="mb-3" />
                                            <p className="fs-5">No vehicles found matching criteria</p>
                                        </div>
                                    </div>
                                )}

                                {/* Pagination */}
                                {lastPage > 1 && (
                                    <div className="d-flex justify-content-center mt-4">
                                        <nav aria-label="Page navigation example">
                                            <ul className="pagination pagination-primary">
                                                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                                    <a className="page-link" href="#javascript" onClick={() => handlePageChange(currentPage - 1)}>Previous</a>
                                                </li>
                                                {[...Array(lastPage)].map((_, i) => {
                                                    const pageNum = i + 1;
                                                    // Basic logic to show limited pages if too many, optional but good for UX
                                                    // For now, simple list as requested or assumed for typical small ranges
                                                    return (
                                                        <li key={pageNum} className={`page-item ${currentPage === pageNum ? 'active' : ''}`}>
                                                            <a className="page-link" href="#javascript" onClick={() => handlePageChange(pageNum)}>{pageNum}</a>
                                                        </li>
                                                    );
                                                })}
                                                <li className={`page-item ${currentPage === lastPage ? 'disabled' : ''}`}>
                                                    <a className="page-link" href="#javascript" onClick={() => handlePageChange(currentPage + 1)}>Next</a>
                                                </li>
                                            </ul>
                                        </nav>
                                    </div>
                                )}
                            </>
                        )}
                    </>
                ) : (
                    <>
                        <div className="d-flex justify-content-between align-items-center mb-4 border-0 shadow-none p-3" style={{ background: 'rgba(255, 255, 255, 0.5)', backdropFilter: 'blur(10px)', borderRadius: '15px', position: 'relative', zIndex: 5 }}>
                            <h5 className="mb-0 fw-bold"></h5>
                            <div className="d-flex gap-2 align-items-center">
                                <Button
                                    color="info"
                                    className="d-flex align-items-center gap-2 px-3 py-2 rounded-pill shadow-sm text-white"
                                    onClick={() => setSelectedVehicle('')}
                                >
                                    <ArrowLeft size={16} /> <span className="fw-bold d-none d-sm-inline">Back</span>
                                </Button>
                                {hasPermission('vehicle-report.download') && (
                                    <Button
                                        color="success"
                                        className="d-flex align-items-center gap-2 px-3 py-2 rounded-pill shadow-sm"
                                        onClick={handleDownload}
                                        disabled={!reportData}
                                    >
                                        <Download size={16} /> <span className="fw-bold d-none d-sm-inline">Download</span>
                                    </Button>
                                )}
                                {hasPermission('vehicle-report.print') && (
                                    <Button
                                        color="primary"
                                        className="d-flex align-items-center gap-2 px-3 py-2 rounded-pill shadow-sm"
                                        onClick={handlePrint}
                                        disabled={!reportData}
                                    >
                                        <Printer size={16} /> <span className="fw-bold d-none d-sm-inline">Print</span>
                                    </Button>
                                )}
                            </div>
                        </div>

                        <Card className="shadow-none border-0 p-3">
                            <CardBody className="px-0">
                                {reportLoading || !reportData ? (
                                    <div className="d-flex justify-content-center align-items-center w-100" style={{ minHeight: '50vh' }}><Spinner color="primary" /></div>
                                ) : (
                                    <div ref={componentRef} className="px-4 bg-white">
                                        <div className="d-flex justify-content-between align-items-center mb-4">
                                            <H5>Vehicle Report: {reportData.vehicleInfo.regNo}</H5>
                                            <span className="text-muted small">Generated on: {new Date().toLocaleDateString('en-GB')}</span>
                                        </div>
                                        <Row className="mb-4 g-3">
                                            <Col md={3}>
                                                <Card className="h-100 border-0 shadow-sm" style={{ background: 'linear-gradient(135deg, #fff5f5 0%, #fff 100%)', borderLeft: '4px solid #dc3545' }}>
                                                    <CardBody className="p-4 d-flex align-items-center justify-content-between">
                                                        <div>
                                                            <h6 className="text-muted text-uppercase small fw-bold mb-2">Total Expenses</h6>
                                                            <h3 className="text-danger mb-0 fw-bold">{reportData.totalExpenses}</h3>
                                                        </div>
                                                        <div className="p-3 bg-white rounded-circle shadow-sm">
                                                            <TrendingDown size={24} className="text-danger" />
                                                        </div>
                                                    </CardBody>
                                                </Card>
                                            </Col>
                                            <Col md={3}>
                                                <Card className="h-100 border-0 shadow-sm" style={{ background: 'linear-gradient(135deg, #f0f7ff 0%, #fff 100%)', borderLeft: '4px solid #0d6efd' }}>
                                                    <CardBody className="p-4 d-flex align-items-center justify-content-between">
                                                        <div>
                                                            <h6 className="text-muted text-uppercase small fw-bold mb-2">Vehicle Year</h6>
                                                            <h3 className="text-primary mb-0 fw-bold">{reportData.vehicleInfo.year}</h3>
                                                        </div>
                                                        <div className="p-3 bg-white rounded-circle shadow-sm">
                                                            <Calendar size={24} className="text-primary" />
                                                        </div>
                                                    </CardBody>
                                                </Card>
                                            </Col>
                                            <Col md={3}>
                                                <Card className="h-100 border-0 shadow-sm" style={{ background: 'linear-gradient(135deg, #fff8e6 0%, #fff 100%)', borderLeft: '4px solid #ffc107' }}>
                                                    <CardBody className="p-4 d-flex align-items-center justify-content-between">
                                                        <div>
                                                            <h6 className="text-muted text-uppercase small fw-bold mb-2">Branch</h6>
                                                            <h4 className="text-dark mb-0 fw-bold text-truncate" title={reportData.vehicleInfo.branch}>{reportData.vehicleInfo.branch}</h4>
                                                        </div>
                                                        <div className="p-3 bg-white rounded-circle shadow-sm">
                                                            <MapPin size={24} className="text-warning" />
                                                        </div>
                                                    </CardBody>
                                                </Card>
                                            </Col>
                                            <Col md={3}>
                                                <Card className="h-100 border-0 shadow-sm" style={{ background: 'linear-gradient(135deg, #e6fffa 0%, #fff 100%)', borderLeft: '4px solid #198754' }}>
                                                    <CardBody className="p-4 d-flex align-items-center justify-content-between">
                                                        <div>
                                                            <h6 className="text-muted text-uppercase small fw-bold mb-2">Current Status</h6>
                                                            <Badge color={reportData.vehicleInfo.status === 'Active' ? 'success' : 'warning'} className="p-2 px-3 rounded-pill">
                                                                {reportData.vehicleInfo.status}
                                                            </Badge>
                                                        </div>
                                                        <div className="p-3 bg-white rounded-circle shadow-sm">
                                                            {reportData.vehicleInfo.status === 'Active' ? <CheckCircle size={24} className="text-success" /> : <Tool size={24} className="text-warning" />}
                                                        </div>
                                                    </CardBody>
                                                </Card>
                                            </Col>
                                        </Row>

                                        {/* Detailed Vehicle Info Section */}
                                        <Card className="mb-4 border shadow-sm">
                                            <CardHeader className="bg-light py-3">
                                                <H6 className="mb-0 text-primary">Vehicle Details</H6>
                                            </CardHeader>
                                            <CardBody className="py-4">
                                                <Row className="gy-4">
                                                    <Col md={3} sm={6}>
                                                        <div className="d-flex flex-column">
                                                            <span className="d-flex align-items-center text-muted small fw-bold text-uppercase mb-2 gap-2"><Truck size={14} /> Brand & Model</span>
                                                            <span className="fw-bold fs-6 text-dark">{reportData.vehicleInfo.make} {reportData.vehicleInfo.model}</span>
                                                        </div>
                                                    </Col>
                                                    <Col md={3} sm={6}>
                                                        <div className="d-flex flex-column">
                                                            <span className="d-flex align-items-center text-muted small fw-bold text-uppercase mb-2 gap-2"><FileText size={14} /> VIN / Chassis No</span>
                                                            <span className="fw-bold fs-6 text-dark text-break header-font">{reportData.vehicleInfo.vin || 'N/A'}</span>
                                                        </div>
                                                    </Col>
                                                    <Col md={3} sm={6}>
                                                        <div className="d-flex flex-column">
                                                            <span className="d-flex align-items-center text-muted small fw-bold text-uppercase mb-2 gap-2"><Tool size={14} /> Fuel & Color</span>
                                                            <span className="fw-bold fs-6 text-dark">{reportData.vehicleInfo.fuel_type} • {reportData.vehicleInfo.color}</span>
                                                        </div>
                                                    </Col>
                                                    <Col md={3} sm={6}>
                                                        <div className="d-flex flex-column">
                                                            <span className="d-flex align-items-center text-muted small fw-bold text-uppercase mb-2 gap-2"><Grid size={14} /> Category</span>
                                                            <span className="fw-bold fs-6 text-dark">{reportData.vehicleInfo.category}</span>
                                                        </div>
                                                    </Col>

                                                    <Col md={12}><hr className="my-1 border-light" /></Col>

                                                    <Col md={3} sm={6}>
                                                        <div className="d-flex flex-column">
                                                            <span className="d-flex align-items-center text-muted small fw-bold text-uppercase mb-2 gap-2"><Calendar size={14} /> Registration Expiry</span>
                                                            <span className={`fw-bold fs-6 ${new Date(reportData.vehicleInfo.reg_expiry) < new Date() ? 'text-danger' : 'text-dark'}`}>
                                                                {reportData.vehicleInfo.reg_expiry ? new Date(reportData.vehicleInfo.reg_expiry).toLocaleDateString('en-GB') : 'N/A'}
                                                            </span>
                                                        </div>
                                                    </Col>
                                                    <Col md={3} sm={6}>
                                                        <div className="d-flex flex-column">
                                                            <span className="d-flex align-items-center text-muted small fw-bold text-uppercase mb-2 gap-2"><DollarSign size={14} /> Purchase Date</span>
                                                            <span className="fw-bold fs-6 text-dark">{reportData.vehicleInfo.purchase_date ? new Date(reportData.vehicleInfo.purchase_date).toLocaleDateString('en-GB') : 'N/A'}</span>
                                                        </div>
                                                    </Col>
                                                    <Col md={3} sm={6}>
                                                        <div className="d-flex flex-column">
                                                            <span className="d-flex align-items-center text-muted small fw-bold text-uppercase mb-2 gap-2"><Shield size={14} /> Insurance Provider</span>
                                                            <span className="fw-bold fs-6 text-dark">{reportData.vehicleInfo.ins_provider}</span>
                                                        </div>
                                                    </Col>
                                                    <Col md={3} sm={6}>
                                                        <div className="d-flex flex-column">
                                                            <span className="d-flex align-items-center text-muted small fw-bold text-uppercase mb-2 gap-2"><FileText size={14} /> Insurance Policy</span>
                                                            <span className="fw-bold fs-6 text-dark">{reportData.vehicleInfo.ins_policy || 'N/A'}</span>
                                                        </div>
                                                    </Col>

                                                    <Col md={12}><hr className="my-1 border-light" /></Col>

                                                    <Col md={4} sm={6}>
                                                        <div className="d-flex flex-column">
                                                            <span className="d-flex align-items-center text-muted small fw-bold text-uppercase mb-2 gap-2"><User size={14} /> Owner Name</span>
                                                            <span className="fw-bold fs-6 text-primary">{reportData.vehicleInfo.owner_name}</span>
                                                        </div>
                                                    </Col>
                                                    <Col md={4} sm={6}>
                                                        <div className="d-flex flex-column">
                                                            <span className="d-flex align-items-center text-muted small fw-bold text-uppercase mb-2 gap-2"><Phone size={14} /> Owner Contact</span>
                                                            <span className="fw-bold fs-6 text-dark">{reportData.vehicleInfo.owner_mobile}</span>
                                                        </div>
                                                    </Col>
                                                    <Col md={4} sm={6}>
                                                        <div className="d-flex flex-column">
                                                            <span className="d-flex align-items-center text-muted small fw-bold text-uppercase mb-2 gap-2"><Mail size={14} /> Owner Email</span>
                                                            <span className="fw-bold fs-6 text-dark">{reportData.vehicleInfo.owner_email}</span>
                                                        </div>
                                                    </Col>
                                                </Row>
                                            </CardBody>
                                        </Card>



                                        <Row>
                                            <Col md={7} className="mb-4">
                                                <Card className="h-100 border shadow-sm">
                                                    <CardBody>
                                                        <div className="d-flex align-items-center mb-4">
                                                            <div className="p-2 bg-light rounded me-3"><DollarSign className="text-success" size={20} /></div>
                                                            <H5 className="mb-0">Expense History</H5>
                                                        </div>
                                                        <div className="table-responsive">
                                                            <Table hover size="sm" className="align-middle">
                                                                <thead className="table-light"><tr><th>Date</th><th>Vehicle No</th><th>Type</th><th>Amount</th></tr></thead>
                                                                <tbody>
                                                                    {(!reportData.expenses || reportData.expenses.length === 0) && <tr><td colSpan={4} className="text-center py-4 text-muted">No Expenses Found</td></tr>}
                                                                    {reportData.expenses && reportData.expenses.map((e: any, i: number) => (
                                                                        <tr key={i}>
                                                                            <td>{e.date ? new Date(e.date).toLocaleDateString('en-GB') : '-'}</td>
                                                                            <td className="fw-bold text-secondary">{e.reg_no}</td>
                                                                            <td>
                                                                                <Badge color={
                                                                                    e.type === 'Fuel Expense' ? 'danger' :
                                                                                        (e.type === 'Insurance Renewal' ? 'success' :
                                                                                            (e.type === 'RC Renewal' ? 'info' :
                                                                                                (e.type === 'Maintenance / Service' ? 'warning' :
                                                                                                    (e.type === 'LPO' ? 'primary' :
                                                                                                        (e.type === 'Accident' ? 'dark' : 'secondary')))))
                                                                                } className="px-2">{e.type}</Badge>
                                                                                {e.category && <small className="d-block text-muted">{e.category}</small>}
                                                                            </td>
                                                                            <td className="text-danger fw-bold">{e.amount}</td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </Table>
                                                        </div>
                                                    </CardBody>
                                                </Card>
                                            </Col>
                                            <Col md={5} className="mb-4">
                                                <Card className="h-100 border shadow-sm">
                                                    <CardBody>
                                                        <div className="d-flex align-items-center mb-4">
                                                            <div className="p-2 bg-light rounded me-3"><Calendar className="text-warning" size={20} /></div>
                                                            <H5 className="mb-0">Upcoming Renewals & Services</H5>
                                                        </div>
                                                        <div className="table-responsive">
                                                            <Table hover size="sm" className="align-middle">
                                                                <thead className="table-light"><tr><th>Vehicle No</th><th>Next Date</th><th>Type</th></tr></thead>
                                                                <tbody>
                                                                    {(!reportData.upcoming || reportData.upcoming.length === 0) && <tr><td colSpan={3} className="text-center py-4 text-muted">No Upcoming Events</td></tr>}
                                                                    {reportData.upcoming && reportData.upcoming.map((u: any, i: number) => (
                                                                        <tr key={i}>
                                                                            <td className="fw-bold">{u.reg_no}</td>
                                                                            <td>
                                                                                <span className={new Date(u.date) < new Date() ? 'text-danger fw-bold' : 'text-primary'}>
                                                                                    {u.date ? new Date(u.date).toLocaleDateString('en-GB') : '-'}
                                                                                </span>
                                                                            </td>
                                                                            <td><Badge color="light" className="text-dark p-2">{u.type}</Badge></td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </Table>
                                                        </div>
                                                    </CardBody>
                                                </Card>
                                            </Col>
                                            <Col md={6} className="mb-4">
                                                <Card className="h-100 border shadow-sm">
                                                    <CardBody>
                                                        <div className="d-flex align-items-center mb-4">
                                                            <div className="p-2 bg-light rounded me-3"><User className="text-primary" size={20} /></div>
                                                            <H5 className="mb-0">Driver Usage History</H5>
                                                        </div>
                                                        <Table hover size="sm" responsive className="align-middle">
                                                            <thead className="table-light"><tr><th>Driver</th><th>Range</th><th>Start Odometer</th><th>End Odometer</th><th>Duration</th></tr></thead>
                                                            <tbody>
                                                                {reportData.driverHistory.length === 0 && <tr><td colSpan={5} className="text-center py-4 text-muted">No History Found</td></tr>}
                                                                {reportData.driverHistory.map((d: any, i: number) => (
                                                                    <tr key={i}>
                                                                        <td className="fw-bold">{d.driver}</td>
                                                                        <td><small>{d.from}<br />to {d.to}</small></td>
                                                                        <td>{d.start_odometer}</td>
                                                                        <td>{d.end_odometer || '-'}</td>
                                                                        <td><Badge color="info" outline>{d.duration}</Badge></td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </Table>
                                                    </CardBody>
                                                </Card>
                                            </Col>
                                            <Col md={6} className="mb-4">
                                                <Card className="h-100 border shadow-sm">
                                                    <CardBody>
                                                        <div className="d-flex align-items-center mb-4">
                                                            <div className="p-2 bg-light rounded me-3"><AlertTriangle className="text-danger" size={20} /></div>
                                                            <H5 className="mb-0">Accident History</H5>
                                                        </div>
                                                        <Table hover size="sm" responsive className="align-middle">
                                                            <thead className="table-light"><tr><th>Date</th><th>Description</th><th>Cost</th></tr></thead>
                                                            <tbody>
                                                                {reportData.accidents.length === 0 && <tr><td colSpan={3} className="text-center py-4 text-muted">No Accidents Found</td></tr>}
                                                                {reportData.accidents.map((a: any, i: number) => (
                                                                    <tr key={i}><td>{a.date ? new Date(a.date).toLocaleDateString('en-GB') : '-'}</td><td>{a.description}</td><td className="text-danger fw-bold">{a.cost}</td></tr>
                                                                ))}
                                                            </tbody>
                                                        </Table>
                                                    </CardBody>
                                                </Card>
                                            </Col>

                                        </Row>

                                    </div>
                                )}
                            </CardBody>
                        </Card>
                    </>
                )}
            </div>
        </div >
    );
};

export default VehicleWiseReport;
